<template>
  <div class="competition-archive-management-page">
    <div class="archive-header">
      <div class="archive-header-inner">
        <el-button type="text" class="back-btn" icon="el-icon-arrow-left" @click="goBack">返回</el-button>
        <div>
          <h1 class="archive-page-title">{{ pageTitle }}</h1>
          <p class="archive-page-subtitle">{{ pageSubtitle }}</p>
        </div>
      </div>
    </div>

    <div class="archive-body" v-loading="loading">
      <el-row :gutter="20" class="stat-row">
        <el-col :lg="6" :md="12" :sm="24">
          <el-card class="stat-card" shadow="never">
            <div class="stat-label">档案总数</div>
            <div class="stat-value">{{ summaryStats.total }}</div>
          </el-card>
        </el-col>
        <el-col :lg="6" :md="12" :sm="24">
          <el-card class="stat-card" shadow="never">
            <div class="stat-label">未审核</div>
            <div class="stat-value stat-warning">{{ summaryStats.pending }}</div>
          </el-card>
        </el-col>
        <el-col :lg="6" :md="12" :sm="24">
          <el-card class="stat-card" shadow="never">
            <div class="stat-label">已审核(通过)</div>
            <div class="stat-value stat-success">{{ summaryStats.passed }}</div>
          </el-card>
        </el-col>
        <el-col :lg="6" :md="12" :sm="24">
          <el-card class="stat-card" shadow="never">
            <div class="stat-label">已审核(驳回)</div>
            <div class="stat-value stat-danger">{{ summaryStats.rejected }}</div>
          </el-card>
        </el-col>
      </el-row>

      <el-card class="archive-card" shadow="never">
        <div slot="header" class="card-header">
          <span>{{ listTitle }}</span>
          <div class="filter-bar">
            <el-input
              v-model="keyword"
              clearable
              prefix-icon="el-icon-search"
              placeholder="搜索学生姓名 / 学号 / 竞赛名称 / 获奖结果"
              class="filter-input"
              @clear="handleFilterChange"
              @input="handleFilterChange"
            />
            <el-select v-model="reviewStatusFilter" clearable placeholder="审核状态" class="filter-select" @change="handleFilterChange">
              <el-option label="未审核" :value="0" />
              <el-option label="已审核(通过)" :value="1" />
              <el-option label="已审核(驳回)" :value="2" />
            </el-select>
            <el-button icon="el-icon-refresh" @click="reloadArchives">刷新</el-button>
          </div>
        </div>

        <el-table
          :data="pagedArchives"
          border
          fit
          highlight-current-row
          :empty-text="emptyText"
          :header-cell-style="{
            background: '#f2f3f4',
            color: '#555',
            'font-weight': 'bold',
            'line-height': '32px'
          }"
        >
          <el-table-column label="序号" align="center" width="80">
            <template slot-scope="scope">
              {{ (pageNum - 1) * pageSize + scope.$index + 1 }}
            </template>
          </el-table-column>
          <el-table-column prop="studentName" label="学生姓名" min-width="130" align="center" />
          <el-table-column prop="studentId" label="学号/ID" min-width="110" align="center" />
          <el-table-column prop="competitionName" label="竞赛名称" min-width="180" show-overflow-tooltip />
          <el-table-column prop="competitionYear" label="参赛年份" width="100" align="center" />
          <el-table-column prop="awardResult" label="获奖结果" min-width="160" show-overflow-tooltip />
          <el-table-column label="审核状态" width="150" align="center">
            <template slot-scope="scope">
              <el-tag :type="reviewStatusType(scope.row.reviewStatus)">
                {{ reviewStatusLabel(scope.row.reviewStatus) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="审核时间" min-width="160" align="center">
            <template slot-scope="scope">
              {{ formatDateTime(scope.row.reviewTime) }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="140" align="center">
            <template slot-scope="scope">
              <el-button type="text" size="small" @click="openDetail(scope.row)">{{ canReview ? '查看/审核' : '查看详情' }}</el-button>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-container">
          <el-pagination
            :current-page="pageNum"
            :page-sizes="[10, 20, 30, 50]"
            :page-size="pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="filteredArchives.length"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </el-card>
    </div>

    <el-dialog :visible.sync="detailDialogVisible" :title="detailDialogTitle" width="980px" top="4vh" :close-on-click-modal="false">
      <div v-loading="detailLoading" class="detail-dialog-body">
        <template v-if="currentArchive">
          <div class="detail-status-bar">
            <el-tag :type="reviewStatusType(currentArchive.reviewStatus)">
              {{ reviewStatusLabel(currentArchive.reviewStatus) }}
            </el-tag>
            <span v-if="currentArchive.reviewTime" class="detail-status-time">审核时间：{{ formatDateTime(currentArchive.reviewTime) }}</span>
          </div>

          <el-row :gutter="16" class="detail-grid">
            <el-col :span="12"><div class="detail-item"><span class="label">学生姓名</span><span class="value">{{ currentArchive.studentName || '-' }}</span></div></el-col>
            <el-col :span="12"><div class="detail-item"><span class="label">学生ID</span><span class="value">{{ currentArchive.studentId || '-' }}</span></div></el-col>
            <el-col :span="12"><div class="detail-item"><span class="label">竞赛名称</span><span class="value">{{ currentArchive.competitionName || '-' }}</span></div></el-col>
            <el-col :span="12"><div class="detail-item"><span class="label">主办方</span><span class="value">{{ currentArchive.organizer || '-' }}</span></div></el-col>
            <el-col :span="12"><div class="detail-item"><span class="label">竞赛分类</span><span class="value">{{ currentArchive.competitionCategory || '-' }}</span></div></el-col>
            <el-col :span="12"><div class="detail-item"><span class="label">竞赛级别</span><span class="value">{{ currentArchive.competitionLevel || '-' }}</span></div></el-col>
            <el-col :span="12"><div class="detail-item"><span class="label">参赛组别</span><span class="value">{{ currentArchive.groupName || '-' }}</span></div></el-col>
            <el-col :span="12"><div class="detail-item"><span class="label">参赛年份</span><span class="value">{{ currentArchive.competitionYear || '-' }}</span></div></el-col>
            <el-col :span="12"><div class="detail-item"><span class="label">参与日期</span><span class="value">{{ formatDate(currentArchive.participationDate) }}</span></div></el-col>
            <el-col :span="12"><div class="detail-item"><span class="label">获奖等级</span><span class="value">{{ currentArchive.awardLevel || '-' }}</span></div></el-col>
            <el-col :span="24"><div class="detail-item detail-item--full"><span class="label">获奖结果</span><span class="value">{{ currentArchive.awardResult || '-' }}</span></div></el-col>
            <el-col :span="24"><div class="detail-item detail-item--full"><span class="label">项目说明</span><span class="value whitespace-prewrap">{{ currentArchive.description || '-' }}</span></div></el-col>
          </el-row>

          <div class="section-block">
            <div class="section-block-header">
              <h3>上传材料</h3>
            </div>
            <div v-if="currentArchive.attachments && currentArchive.attachments.length" class="attachment-list">
              <div v-for="item in currentArchive.attachments" :key="item.id || item.filePath || item.fileName" class="archive-sub-card">
                <div class="sub-card-title">{{ item.fileName || '未命名文件' }}</div>
                <div class="sub-card-meta">
                  <span>类型：{{ item.bizType || '-' }}</span>
                  <span>文件类型：{{ item.fileType || '-' }}</span>
                  <span>大小：{{ formatFileSize(item.fileSize) }}</span>
                </div>
                <div class="sub-card-actions" v-if="item.filePath || item.fileUrl">
                  <el-button type="text" size="small" @click="openAttachmentPreview(item)">预览</el-button>
                  <a :href="normalizePreviewUrl(item)" target="_blank" rel="noopener noreferrer">新窗口打开</a>
                </div>
              </div>
            </div>
            <el-empty v-else description="暂无上传材料"></el-empty>
          </div>

          <div class="section-block">
            <div class="section-block-header">
              <h3>参与活动记录</h3>
            </div>
            <div v-if="currentArchive.activities && currentArchive.activities.length" class="activity-list">
              <div v-for="item in currentArchive.activities" :key="item.id || `${item.title}-${item.activityDate}`" class="archive-sub-card">
                <div class="sub-card-title">{{ item.title || '未命名活动' }}</div>
                <div class="sub-card-meta">
                  <span>类型：{{ item.activityType || '-' }}</span>
                  <span>日期：{{ formatDate(item.activityDate) }}</span>
                </div>
                <div class="sub-card-desc">{{ item.description || '暂无描述' }}</div>
              </div>
            </div>
            <el-empty v-else description="暂无活动记录"></el-empty>
          </div>

          <div v-if="canReview" class="section-block review-block">
            <div class="section-block-header">
              <h3>审核操作</h3>
            </div>
            <el-form label-width="100px">
              <el-form-item label="审核意见">
                <el-input
                  v-model="reviewForm.reviewComment"
                  type="textarea"
                  :rows="4"
                  placeholder="请输入审核意见，驳回时建议写明原因"
                />
              </el-form-item>
            </el-form>
            <div class="review-actions">
              <el-button type="success" :loading="reviewing" @click="handleReview(1)">审核通过</el-button>
              <el-button type="danger" :loading="reviewing" @click="handleReview(2)">审核驳回</el-button>
            </div>
          </div>
        </template>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="detailDialogVisible = false">关 闭</el-button>
      </div>
    </el-dialog>

    <el-dialog :visible.sync="previewDialogVisible" :title="previewDialogTitle" width="960px" top="5vh">
      <div class="preview-dialog-body">
        <template v-if="previewAttachment">
          <div v-if="previewType === 'image'" class="preview-image-wrap">
            <img :src="previewUrl" :alt="previewAttachment.fileName || '附件预览'" class="preview-image" />
          </div>
          <div v-else-if="previewType === 'pdf'" class="preview-frame-wrap">
            <iframe :src="previewUrl" class="preview-frame" frameborder="0"></iframe>
          </div>
          <div v-else-if="previewType === 'video'" class="preview-video-wrap">
            <video :src="previewUrl" class="preview-video" controls>
              您的浏览器暂不支持视频预览
            </video>
          </div>
          <div v-else class="preview-fallback">
            <p>该附件暂不支持站内直接预览，请点击下方按钮在新窗口打开。</p>
            <a :href="previewUrl" target="_blank" rel="noopener noreferrer">
              <el-button type="primary">新窗口打开</el-button>
            </a>
          </div>
        </template>
      </div>
      <div slot="footer" class="dialog-footer">
        <a v-if="previewUrl" :href="previewUrl" target="_blank" rel="noopener noreferrer">
          <el-button>新窗口打开</el-button>
        </a>
        <el-button type="primary" @click="previewDialogVisible = false">关 闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getCompetitionArchiveOverview,
  getCompetitionArchiveDetail,
  reviewCompetitionArchive
} from '@/api/competitionArchive'

export default {
  name: 'CompetitionArchiveManagement',
  data() {
    return {
      loading: false,
      archives: [],
      linkedStudents: [],
      currentRole: '',
      keyword: '',
      reviewStatusFilter: '',
      pageNum: 1,
      pageSize: 10,
      overviewRequestId: 0,
      detailDialogVisible: false,
      detailLoading: false,
      currentArchive: null,
      reviewing: false,
      reviewForm: {
        reviewComment: ''
      },
      previewDialogVisible: false,
      previewAttachment: null
    }
  },
  computed: {
    isOrganizationView() {
      return this.currentRole === 'role_organization' || (this.$route && this.$route.path && this.$route.path.indexOf('/org/') === 0)
    },
    isParentView() {
      return this.currentRole === 'role_parent' || (this.$route && this.$route.path && this.$route.path.indexOf('/parent-competition-archive') === 0)
    },
    canReview() {
      return this.currentRole === 'role_admin' || this.currentRole === 'role_organization'
    },
    pageTitle() {
      if (this.isOrganizationView) return '资料查验'
      if (this.isParentView) return '学生资料查看'
      return '学生资料审核'
    },
    pageSubtitle() {
      if (this.isOrganizationView) {
        return '机构统一查看绑定学生的竞赛档案，支持关键词、审核状态筛选与单条审核'
      }
      if (this.isParentView) {
        return '家长可按条查看绑定学生的竞赛档案，支持关键词和审核状态筛选，并可直接预览上传附件'
      }
      return '管理员统一查看所有学生竞赛档案，支持关键词、审核状态筛选与单条审核'
    },
    listTitle() {
      return this.canReview ? '审核列表' : '档案列表'
    },
    filteredArchives() {
      const list = Array.isArray(this.archives) ? this.archives : []
      const kw = String(this.keyword || '').trim().toLowerCase()
      const statusFilter = this.reviewStatusFilter === '' || this.reviewStatusFilter === null ? null : Number(this.reviewStatusFilter)
      return list.filter(item => {
        const status = Number(item.reviewStatus || 0)
        if (statusFilter !== null && status !== statusFilter) {
          return false
        }
        if (!kw) {
          return true
        }
        const studentName = String(item.studentName || '').toLowerCase()
        const studentId = String(item.studentId || '').toLowerCase()
        const competitionName = String(item.competitionName || '').toLowerCase()
        const awardResult = String(item.awardResult || '').toLowerCase()
        const organizer = String(item.organizer || '').toLowerCase()
        const groupName = String(item.groupName || '').toLowerCase()
        return [studentName, studentId, competitionName, awardResult, organizer, groupName].some(field => field.includes(kw))
      })
    },
    pagedArchives() {
      const start = (this.pageNum - 1) * this.pageSize
      return this.filteredArchives.slice(start, start + this.pageSize)
    },
    summaryStats() {
      const list = Array.isArray(this.archives) ? this.archives : []
      return {
        total: list.length,
        pending: list.filter(item => Number(item.reviewStatus) === 0).length,
        passed: list.filter(item => Number(item.reviewStatus) === 1).length,
        rejected: list.filter(item => Number(item.reviewStatus) === 2).length
      }
    },
    detailDialogTitle() {
      return this.currentArchive
        ? `${this.canReview ? '审核详情' : '档案详情'} - ${this.currentArchive.studentName || '未命名学生'} · ${this.currentArchive.competitionName || '未命名竞赛'}`
        : (this.canReview ? '审核详情' : '档案详情')
    },
    emptyText() {
      const hasFilters = !!this.keyword || (this.reviewStatusFilter !== '' && this.reviewStatusFilter !== null)
      if (hasFilters) {
        return '没有符合筛选条件的档案'
      }
      if (this.isOrganizationView && !this.linkedStudents.length) {
        return '当前机构暂无绑定学生'
      }
      if (this.isOrganizationView) {
        return '暂无可查看资料'
      }
      if (this.isParentView) {
        return this.linkedStudents.length ? '暂无可查看资料' : '当前账号暂无绑定学生'
      }
      return '暂无竞赛档案'
    },
    previewUrl() {
      return this.previewAttachment ? this.normalizePreviewUrl(this.previewAttachment) : ''
    },
    previewType() {
      const item = this.previewAttachment
      if (!item) return ''
      const mime = String(item.mimeType || '').toLowerCase()
      const fileType = String(item.fileType || '').toLowerCase()
      if (mime.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(fileType)) return 'image'
      if (mime === 'application/pdf' || fileType === 'pdf') return 'pdf'
      if (mime.startsWith('video/') || ['mp4', 'webm', 'ogg', 'mov', 'm4v'].includes(fileType)) return 'video'
      return 'other'
    },
    previewDialogTitle() {
      return this.previewAttachment ? `附件预览 - ${this.previewAttachment.fileName || '未命名文件'}` : '附件预览'
    }
  },
  created() {
    this.loadArchives()
  },
  methods: {
    goBack() {
      if (window.history.length > 1) {
        this.$router.back()
        return
      }
      this.$router.push(this.isOrganizationView ? '/org/dashboard' : '/dashboard')
    },
    handleFilterChange() {
      this.pageNum = 1
    },
    handleSizeChange(val) {
      this.pageSize = val
      this.pageNum = 1
    },
    handleCurrentChange(val) {
      this.pageNum = val
    },
    async reloadArchives() {
      await this.loadArchives()
    },
    async loadArchives() {
      this.loading = true
      const requestId = ++this.overviewRequestId
      try {
        const res = await getCompetitionArchiveOverview(null)
        if (requestId !== this.overviewRequestId) {
          return
        }
        if (res && (res.code === 1 || res.code === 200)) {
          const data = res.data || {}
          this.archives = Array.isArray(data.archives) ? data.archives : []
          this.linkedStudents = Array.isArray(data.linkedStudents) ? data.linkedStudents : []
          this.currentRole = data.currentRole || ''
          if (this.pageNum > 1 && this.pagedArchives.length === 0 && this.filteredArchives.length > 0) {
            this.pageNum = 1
          }
        } else {
          this.$message.error((res && res.msg) || '获取档案失败')
        }
      } catch (e) {
        console.error('获取档案失败:', e)
        this.$message.error('获取档案失败')
      } finally {
        if (requestId === this.overviewRequestId) {
          this.loading = false
        }
      }
    },
    async openDetail(row) {
      if (!row || !row.id) {
        return
      }
      this.detailDialogVisible = true
      this.detailLoading = true
      this.currentArchive = null
      try {
        const res = await getCompetitionArchiveDetail(row.id)
        if (res && (res.code === 1 || res.code === 200) && res.data) {
          this.currentArchive = res.data
          this.reviewForm.reviewComment = res.data.reviewComment || ''
        } else {
          this.$message.error((res && res.msg) || '获取档案详情失败')
        }
      } catch (e) {
        console.error('获取档案详情失败:', e)
        this.$message.error('获取档案详情失败')
      } finally {
        this.detailLoading = false
      }
    },
    openAttachmentPreview(item) {
      if (!item || !(item.filePath || item.fileUrl)) {
        return
      }
      this.previewAttachment = { ...item }
      this.previewDialogVisible = true
    },
    async handleReview(reviewStatus) {
      if (!this.canReview || !this.currentArchive || !this.currentArchive.id) {
        return
      }
      this.reviewing = true
      try {
        const res = await reviewCompetitionArchive({
          archiveId: this.currentArchive.id,
          reviewStatus,
          reviewComment: this.reviewForm.reviewComment
        })
        if (res && (res.code === 1 || res.code === 200)) {
          this.$message.success('审核成功')
          await this.loadArchives()
          await this.openDetail({ id: this.currentArchive.id })
        } else {
          this.$message.error((res && res.msg) || '审核失败')
        }
      } catch (e) {
        console.error('审核失败:', e)
        this.$message.error('审核失败')
      } finally {
        this.reviewing = false
      }
    },
    reviewStatusLabel(status) {
      const value = Number(status)
      if (value === 1) return '已审核(通过)'
      if (value === 2) return '已审核(驳回)'
      return '未审核'
    },
    reviewStatusType(status) {
      const value = Number(status)
      if (value === 1) return 'success'
      if (value === 2) return 'danger'
      return 'warning'
    },
    formatDate(value) {
      if (!value) return '-'
      const s = String(value)
      return s.length >= 10 ? s.slice(0, 10).replace(/T.*/, '') : s
    },
    formatDateTime(value) {
      if (!value) return '-'
      const s = String(value)
      return s.length >= 19 ? s.slice(0, 19).replace('T', ' ') : s.replace('T', ' ')
    },
    formatFileSize(size) {
      const n = Number(size)
      if (!Number.isFinite(n) || n <= 0) return '-'
      if (n < 1024) return `${n} B`
      if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
      return `${(n / (1024 * 1024)).toFixed(1)} MB`
    },
    normalizePreviewUrl(item) {
      if (item.fileUrl && String(item.fileUrl).startsWith('http')) {
        return item.fileUrl
      }
      const path = item.filePath || ''
      return `/api/file/preview?path=${encodeURIComponent(path)}`
    }
  }
}
</script>

<style scoped>
.competition-archive-management-page {
  min-height: 100vh;
  background: #f5f7fa;
}

.archive-header {
  background: linear-gradient(135deg, #1f3c88 0%, #39a0ed 100%);
  padding: 24px 0;
}

.archive-header-inner,
.archive-body {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

.archive-header-inner {
  display: flex;
  align-items: center;
  gap: 16px;
}

.back-btn,
.archive-page-title,
.archive-page-subtitle {
  color: #fff;
}

.archive-page-title {
  font-size: 30px;
  font-weight: 700;
  margin: 0 0 8px;
}

.archive-page-subtitle {
  margin: 0;
  opacity: 0.9;
}

.archive-body {
  padding-top: 24px;
  padding-bottom: 40px;
}

.stat-row {
  margin-bottom: 20px;
}

.stat-card,
.archive-card {
  border-radius: 16px;
}

.stat-card {
  min-height: 110px;
}

.stat-label {
  font-size: 13px;
  color: #909399;
  margin-bottom: 10px;
}

.stat-value {
  font-size: 30px;
  font-weight: 700;
  color: #1a1a2e;
}

.stat-warning {
  color: #e6a23c;
}

.stat-success {
  color: #67c23a;
}

.stat-danger {
  color: #f56c6c;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  font-weight: 600;
}

.filter-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.filter-input {
  width: 320px;
}

.filter-select {
  width: 180px;
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
}

.detail-dialog-body {
  max-height: 72vh;
  overflow: auto;
  padding-right: 4px;
}

.detail-status-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.detail-status-time {
  color: #909399;
  font-size: 12px;
}

.detail-grid {
  margin-bottom: 6px;
}

.detail-item {
  display: flex;
  gap: 12px;
  padding: 10px 12px;
  margin-bottom: 12px;
  border-radius: 10px;
  background: #fafafa;
  border: 1px solid #ebeef5;
}

.detail-item--full {
  min-height: 52px;
}

.detail-item .label {
  flex-shrink: 0;
  min-width: 80px;
  color: #909399;
}

.detail-item .value {
  color: #303133;
  flex: 1;
}

.whitespace-prewrap {
  white-space: pre-wrap;
}

.section-block {
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid #f0f0f0;
}

.section-block-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.section-block-header h3 {
  margin: 0;
  font-size: 16px;
  color: #1a1a2e;
}

.archive-sub-card {
  padding: 14px 16px;
  border-radius: 12px;
  background: #fafcff;
  border: 1px solid #edf2f7;
  margin-bottom: 12px;
}

.sub-card-title {
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: 8px;
}

.sub-card-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 12px;
  color: #909399;
}

.sub-card-actions {
  margin-top: 10px;
}

.sub-card-actions a {
  color: #1890ff;
  text-decoration: none;
}

.sub-card-desc {
  margin-top: 10px;
  color: #303133;
  line-height: 1.7;
  white-space: pre-wrap;
}

.review-block {
  background: #fcfcff;
  border-radius: 12px;
  padding: 2px 0 0;
}

.review-actions {
  display: flex;
  gap: 12px;
}

@media (max-width: 768px) {
  .archive-page-title {
    font-size: 24px;
  }

  .archive-header-inner {
    align-items: flex-start;
  }

  .filter-input,
  .filter-select {
    width: 100%;
  }

  .filter-bar {
    width: 100%;
  }
}
</style>
